/**
 * Hours display — the single formatter for every hour figure the app shows.
 *
 * Time is entered in quarter hours, so a total like 40.75h is exact and must
 * read as "40.75", never "40.8". Every hour figure — the timesheet grid, the
 * approvals inbox and drawer, the project dialogs, the admin users pane and the
 * API's anomaly labels — goes through here so a one-decimal copy cannot creep
 * back in at one call site (issue #121). It lives in `shared` because both
 * `packages/web` and `packages/api` render hours and neither may import the
 * other (see
 * `docs/solutions/conventions/two-implementations-of-one-concept-diverge-at-the-edge-2026-09-08.md`).
 */

export const MINUTES_PER_HOUR = 60;

/** Two places is exact for quarter hours (.25 / .5 / .75). */
const HOUR_DECIMAL_PLACES = 2;
const HOUR_ROUNDING_FACTOR = 10 ** HOUR_DECIMAL_PLACES;

/**
 * Round an hour value to two decimals, absorbing float noise
 * (`0.1 + 0.2` → `0.3`). Quarter-hour values pass through unchanged.
 */
export function roundHours(hours: number): number {
  const rounded = Math.round(hours * HOUR_ROUNDING_FACTOR) / HOUR_ROUNDING_FACTOR;
  // Normalise -0 so it never renders as "-0".
  return rounded === 0 ? 0 : rounded;
}

/** Minutes → hours as a number, rounded to two decimals. */
export function minutesToHours(minutes: number): number {
  return roundHours(minutes / MINUTES_PER_HOUR);
}

/**
 * Format an hour value with up to two decimals and no trailing zeros:
 * `40` → "40", `40.5` → "40.5", `40.75` → "40.75". No unit suffix — callers
 * add "h" or "hours" to suit the copy.
 */
export function formatHourValue(hours: number): string {
  return String(roundHours(hours));
}

/** Format a minute count as hours — see {@link formatHourValue}. */
export function formatHours(minutes: number): string {
  return formatHourValue(minutes / MINUTES_PER_HOUR);
}
